import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Exam, ExamAttempt } from '../../types';
import { api } from '../../services/api';
import { emitPointsUpdated } from '../../events/sessionEvents';
import MarkdownContent from '../shared/MarkdownContent';

const formatRemainingTime = (seconds: number): string => {
  const safeSeconds = Math.max(0, seconds);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const secs = safeSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${minutes}:${String(secs).padStart(2, '0')}`;
};

type MarkingQuestionResult = NonNullable<NonNullable<ExamAttempt['marking_data']>['questions']>[number];

const formatMarks = (marks: number, maxMarks: number): string => (
  `${marks} / ${maxMarks} ${maxMarks === 1 ? 'mark' : 'marks'}`
);

const ExamPage: React.FC = () => {
  const { examId, attemptId: routeAttemptId } = useParams<{ examId: string; attemptId?: string }>();
  const navigate = useNavigate();
  const attemptId = useMemo(() => {
    if (routeAttemptId) return routeAttemptId;
    const randomId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
    return `exam-attempt-${examId || 'unknown'}-${randomId}`;
  }, [examId, routeAttemptId]);
  const [exam, setExam] = useState<Exam | null>(null);
  const [answers, setAnswers] = useState<Record<number, string | string[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasEdited, setHasEdited] = useState(false);
  const [remainingTimeSeconds, setRemainingTimeSeconds] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [attemptState, setAttemptState] = useState<'IN_PROGRESS' | 'SUBMITTED' | 'MARKED'>('IN_PROGRESS');
  const [obtainedMarks, setObtainedMarks] = useState<number | null>(null);
  const [totalMarks, setTotalMarks] = useState<number | null>(null);
  const [markingData, setMarkingData] = useState<ExamAttempt['marking_data']>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showMarkingModal, setShowMarkingModal] = useState(false);
  const [showTimeExpiredModal, setShowTimeExpiredModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionStartedRef = useRef(false);

  const applyAttempt = useCallback((attempt: ExamAttempt) => {
    setAnswers(attempt.data.answers || {});
    setAttemptState(attempt.state);
    setObtainedMarks(attempt.obtained_marks ?? null);
    setTotalMarks(attempt.total_marks ?? null);
    setMarkingData(attempt.marking_data ?? null);
    setShowMarkingModal(attempt.state === 'SUBMITTED');
  }, []);

  useEffect(() => {
    const loadExam = async () => {
      if (!examId) {
        setError('Invalid exam ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const me = await api.me();
        if (!me?.authenticated) {
          try {
            const { course_id } = await api.getExamCourse(examId);
            navigate(`/register?redirect=${encodeURIComponent(`/course/${course_id}`)}`, { replace: true });
          } catch {
            navigate('/register?redirect=/browse', { replace: true });
          }
          return;
        }

        const data = await api.getExam(examId);
        setExam(data);
        const defaultRemainingTime = data.time === null ? null : data.time * 60;
        if (routeAttemptId) {
          try {
            const attempt = await api.getExamAttempt(routeAttemptId);
            if (attempt.exam_id !== examId) {
              throw new Error('Exam attempt does not belong to this exam');
            }
            applyAttempt(attempt);
            setRemainingTimeSeconds(
              typeof attempt.data.remaining_time_seconds === 'number'
                ? attempt.data.remaining_time_seconds
                : defaultRemainingTime
            );
          } catch (e) {
            if (e instanceof Error && e.message === 'Exam attempt not found') {
              setAnswers({});
              setAttemptState('IN_PROGRESS');
              setObtainedMarks(null);
              setTotalMarks(null);
              setMarkingData(null);
              setShowMarkingModal(false);
              setRemainingTimeSeconds(defaultRemainingTime);
            } else {
              throw e;
            }
          }
        } else {
          setAnswers({});
          setAttemptState('IN_PROGRESS');
          setObtainedMarks(null);
          setTotalMarks(null);
          setMarkingData(null);
          setShowMarkingModal(false);
          setRemainingTimeSeconds(defaultRemainingTime);
        }
        setHasEdited(false);
        setIsPaused(false);
        setSubmitError(null);
        setShowTimeExpiredModal(false);
        setIsSubmitting(false);
        submissionStartedRef.current = false;
      } catch (e) {
        console.error('Error loading exam:', e);
        setError('Failed to load exam');
      } finally {
        setLoading(false);
      }
    };

    void loadExam();
  }, [applyAttempt, examId, navigate, routeAttemptId]);

  const questions = exam?.data.questions || [];

  const saveAttempt = useCallback(async (currentAnswers: Record<number, string | string[]>, currentRemainingTimeSeconds: number | null) => {
    if (!examId || !exam || attemptState !== 'IN_PROGRESS') return;
    await api.saveExamAttempt(attemptId, {
      exam_id: examId,
      data: {
        answers: currentAnswers,
        remaining_time_seconds: currentRemainingTimeSeconds,
      },
    });
  }, [attemptId, attemptState, exam, examId]);

  const submitAttempt = useCallback(async (timedOut: boolean) => {
    if (!examId || !exam || attemptState !== 'IN_PROGRESS' || submissionStartedRef.current) return;

    submissionStartedRef.current = true;
    setSubmitError(null);
    setIsSubmitting(true);
    if (timedOut) {
      setShowTimeExpiredModal(true);
      setIsPaused(false);
    } else {
      setShowMarkingModal(true);
    }

    try {
      const result = await api.submitExamAttempt(attemptId, {
        exam_id: examId,
        data: {
          answers,
          remaining_time_seconds: timedOut ? 0 : remainingTimeSeconds,
        },
      });
      applyAttempt(result.exam_attempt);
      emitPointsUpdated();
      setHasEdited(false);
      setIsPaused(false);
    } catch (e) {
      console.error('Failed to submit exam attempt:', e);
      submissionStartedRef.current = false;
      if (!timedOut) {
        setShowMarkingModal(false);
      }
      setSubmitError(e instanceof Error ? e.message : 'Failed to submit exam attempt');
    } finally {
      setIsSubmitting(false);
    }
  }, [answers, applyAttempt, attemptId, attemptState, exam, examId, remainingTimeSeconds]);

  useEffect(() => {
    if (!hasEdited || attemptState !== 'IN_PROGRESS') return;

    const timeout = window.setTimeout(() => {
      saveAttempt(answers, remainingTimeSeconds).catch((e) => {
        console.error('Failed to save exam attempt:', e);
      });
    }, 700);

    return () => window.clearTimeout(timeout);
  }, [answers, attemptState, hasEdited, remainingTimeSeconds, saveAttempt]);

  useEffect(() => {
    if (attemptState !== 'IN_PROGRESS' || isPaused || remainingTimeSeconds === null || remainingTimeSeconds <= 0) return;

    const interval = window.setInterval(() => {
      setRemainingTimeSeconds((prev) => {
        if (prev === null) return null;
        return Math.max(0, prev - 1);
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [attemptState, isPaused, remainingTimeSeconds]);

  useEffect(() => {
    if (attemptState !== 'IN_PROGRESS' || remainingTimeSeconds === null || !exam) return;
    if (remainingTimeSeconds === 0 || remainingTimeSeconds % 15 !== 0) return;

    saveAttempt(answers, remainingTimeSeconds).catch((e) => {
      console.error('Failed to save exam timer:', e);
    });
  }, [answers, attemptState, exam, remainingTimeSeconds, saveAttempt]);

  useEffect(() => {
    if (attemptState !== 'IN_PROGRESS' || remainingTimeSeconds !== 0 || !exam) return;
    void submitAttempt(true);
  }, [attemptState, exam, remainingTimeSeconds, submitAttempt]);

  useEffect(() => {
    if (attemptState !== 'SUBMITTED') return;

    let cancelled = false;
    const interval = window.setInterval(() => {
      api.getExamAttempt(attemptId).then((attempt) => {
        if (cancelled) return;
        applyAttempt(attempt);
        if (attempt.state === 'MARKED') {
          window.clearInterval(interval);
        }
      }).catch((e) => {
        console.error('Failed to poll exam attempt:', e);
      });
    }, 2000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [applyAttempt, attemptId, attemptState]);

  const handleAnswerChange = (index: number, value: string | string[]) => {
    if (attemptState !== 'IN_PROGRESS') return;
    setHasEdited(true);
    setAnswers((prev) => ({ ...prev, [index]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await submitAttempt(false);
  };

  const handlePause = () => {
    setIsPaused(true);
    saveAttempt(answers, remainingTimeSeconds).catch((e) => {
      console.error('Failed to save exam attempt before pause:', e);
    });
  };

  const markingResultsByQuestion = useMemo(() => {
    const results = new Map<number, MarkingQuestionResult>();
    (markingData?.questions || []).forEach((result) => {
      results.set(result.question_index, result);
    });
    return results;
  }, [markingData]);

  if (loading) {
    return <div className="loading">Loading exam...</div>;
  }

  if (error || !exam) {
    return (
      <div className="exam-page">
        <div className="exam-shell">
          <button className="tm-button tm-button--secondary back-btn" onClick={() => navigate(-1)}>Back</button>
          <div className="error">Error: {error || 'Exam not found'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="exam-page">
      <div className={`exam-shell ${isPaused || showMarkingModal || showTimeExpiredModal ? 'exam-shell-paused' : ''}`}>
        <div className="exam-header">
          <button className="tm-button tm-button--secondary back-btn" onClick={() => navigate(-1)}>Back</button>
          <Link to="/" className="exam-home-link">Teach Me Now</Link>
        </div>

        <div className="tm-surface exam-title-block">
          <h1>{exam.name}</h1>
          <p>
            {exam.time === null ? 'Unlimited time' : `${exam.time} minutes`}
          </p>
          {attemptState === 'SUBMITTED' && <p>Submitted for marking.</p>}
          {attemptState === 'MARKED' && obtainedMarks !== null && totalMarks !== null && (
            <p className="exam-results-summary">
              Result: {formatMarks(obtainedMarks, totalMarks)}
            </p>
          )}
        </div>

        {remainingTimeSeconds !== null && attemptState === 'IN_PROGRESS' && (
          <div className="exam-floating-timer" aria-live="polite">
            <div>
              <div className="exam-floating-timer-label">Remaining</div>
              <div className="exam-floating-timer-value">{formatRemainingTime(remainingTimeSeconds)}</div>
            </div>
            <button
              type="button"
              className="exam-pause-btn"
              onClick={handlePause}
            >
              Pause
            </button>
          </div>
        )}

        <form className="exam-form" onSubmit={handleSubmit}>
          {questions.length === 0 ? (
            <div className="empty-state">
              <p>This exam has no questions yet.</p>
            </div>
          ) : (
            questions.map((question, index) => {
              const result = markingResultsByQuestion.get(index);
              const resultClass = attemptState === 'MARKED' && result
                ? result.marks === result.max_marks
                  ? 'exam-question-card-correct'
                  : result.marks > 0
                    ? 'exam-question-card-partial'
                    : 'exam-question-card-incorrect'
                : '';
              return (
              <section key={index} className={`exam-question-card ${resultClass}`}>
                {(() => {
                  const choices = question.choices || [];
                  const hasChoices = choices.length > 0;
                  const allowsMultipleChoices = !!question.allow_multiple;
                  const marks = question.marks || 1;
                  const answer = answers[index];
                  return (
                    <>
                <div className="exam-question-number">
                  Question {index + 1}
                  <span className="exam-question-marks">
                    {attemptState === 'MARKED' && result
                      ? formatMarks(result.marks, result.max_marks)
                      : `${marks} ${marks === 1 ? 'mark' : 'marks'}`}
                  </span>
                </div>
                <div className="exam-question-text">
                  <MarkdownContent content={question.question} />
                </div>

                {hasChoices && (
                  attemptState === 'IN_PROGRESS' ? (
                  <div className="exam-choices">
                    {choices.map((choice, choiceIndex) => (
                      <label key={choiceIndex} className="exam-choice">
                        <input
                          type={allowsMultipleChoices ? 'checkbox' : 'radio'}
                          name={`question-${index}-choice`}
                          value={choice.text}
                          checked={allowsMultipleChoices
                            ? (Array.isArray(answers[index]) && answers[index].includes(choice.text))
                            : answers[index] === choice.text}
                          disabled={attemptState !== 'IN_PROGRESS'}
                          onChange={(e) => {
                            if (!allowsMultipleChoices) {
                              handleAnswerChange(index, e.target.value);
                              return;
                            }
                            const current = Array.isArray(answers[index]) ? answers[index] as string[] : [];
                            const next = e.target.checked
                              ? [...current, choice.text]
                              : current.filter((selected) => selected !== choice.text);
                            handleAnswerChange(index, next);
                          }}
                        />
                        <div className="exam-choice-content">
                          <MarkdownContent content={choice.text} />
                        </div>
                      </label>
                    ))}
                  </div>
                  ) : (
                    <div className="exam-submitted-answer">
                      <div className="exam-answer-label">Your answer</div>
                      {Array.isArray(answer) && answer.length > 0 ? (
                        <div className="exam-submitted-choice-list">
                          {answer.map((selected, selectedIndex) => (
                            <div key={selectedIndex} className="exam-submitted-choice">
                              <MarkdownContent content={selected} />
                            </div>
                          ))}
                        </div>
                      ) : typeof answer === 'string' && answer.trim() ? (
                        <div className="exam-submitted-choice">
                          <MarkdownContent content={answer} />
                        </div>
                      ) : (
                        <div className="exam-submitted-answer-text exam-submitted-answer-empty">
                          No answer submitted.
                        </div>
                      )}
                    </div>
                  )
                )}

                {!hasChoices && (
                  attemptState === 'IN_PROGRESS' ? (
                    <>
                    <label className="exam-answer-label" htmlFor={`exam-answer-${index}`}>
                      Your answer
                    </label>
                    <textarea
                      id={`exam-answer-${index}`}
                      className="exam-answer-box"
                      value={typeof answers[index] === 'string' ? answers[index] : ''}
                      onChange={(e) => handleAnswerChange(index, e.target.value)}
                      disabled={attemptState !== 'IN_PROGRESS'}
                      rows={6}
                    />
                    </>
                  ) : (
                    <div className="exam-submitted-answer">
                      <div className="exam-answer-label">Your answer</div>
                      <div className={`exam-submitted-answer-text ${typeof answer === 'string' && answer.trim() ? '' : 'exam-submitted-answer-empty'}`}>
                        {typeof answer === 'string' && answer.trim() ? answer : 'No answer submitted.'}
                      </div>
                    </div>
                  )
                )}

                {attemptState === 'MARKED' && result && (
                  <div className="exam-feedback">
                    <div className="exam-feedback-label">Feedback</div>
                    <MarkdownContent content={result.feedback} />
                  </div>
                )}
                    </>
                  );
                })()}
              </section>
              );
            })
          )}

          {attemptState === 'IN_PROGRESS' && (
            <div className="exam-submit-row">
              {submitError && <div className="error">Error: {submitError}</div>}
              <button type="submit" className="tm-button tm-button--primary exam-submit-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          )}
        </form>
      </div>

      {isPaused && (
        <div className="exam-pause-backdrop" role="dialog" aria-modal="true" aria-labelledby="exam-pause-title">
          <div className="exam-pause-modal">
            <h2 id="exam-pause-title">Exam paused</h2>
            {remainingTimeSeconds !== null && (
              <p>Remaining time: {formatRemainingTime(remainingTimeSeconds)}</p>
            )}
            <button
              type="button"
              className="tm-button tm-button--primary exam-submit-btn"
              onClick={() => setIsPaused(false)}
            >
              Resume
            </button>
          </div>
        </div>
      )}

      {showTimeExpiredModal && (
        <div className="exam-pause-backdrop" role="dialog" aria-modal="true" aria-labelledby="exam-time-expired-title">
          <div className="exam-pause-modal">
            <h2 id="exam-time-expired-title">Time's up</h2>
            <p>
              {isSubmitting
                ? 'Your time has expired. Your answers are being submitted automatically.'
                : submitError
                  ? `We could not submit your answers automatically: ${submitError}`
                  : 'Your answers were submitted automatically and are being marked.'}
            </p>
            {submitError ? (
              <button
                type="button"
                className="tm-button tm-button--primary exam-submit-btn"
                onClick={() => void submitAttempt(true)}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Try again'}
              </button>
            ) : !isSubmitting ? (
              <button
                type="button"
                className="tm-button tm-button--primary exam-submit-btn"
                onClick={() => setShowTimeExpiredModal(false)}
              >
                Continue
              </button>
            ) : null}
          </div>
        </div>
      )}

      {showMarkingModal && !showTimeExpiredModal && (
        <div className="exam-pause-backdrop" role="dialog" aria-modal="true" aria-labelledby="exam-marking-title">
          <div className="exam-pause-modal exam-marking-modal">
            <h2 id="exam-marking-title">Your solutions are being marked, please wait</h2>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamPage;
